/**
 * The firm's message inbox, and one thread — FR-E8. Redrawn in Wave 15E (C14
 * §10.6; ADR-0033): the kit's `PageHeader`, `Skeleton` while loading, an
 * `EmptyState` with one action, and the internal note tinted from the kit's
 * `TONE_*` rather than a palette colour.
 *
 * Two screens in one file because they are one screen: a list on the left, the
 * thread on the right, and the list's unread count is what the thread clears.
 * It is not a `ListPage`: a thread is read beside the list, not instead of it.
 *
 * **The internal note is rendered so it cannot be mistaken.** A tinted block and
 * a label that says *"the client cannot see this"* — not a small icon, not a
 * different shade of grey. A note that looks like a message is a note somebody
 * writes thinking the client will see it and then writes the next one thinking
 * they will not, and the second mistake is the expensive one.
 *
 * The composer is one box with a switch rather than two tabs, for the same
 * reason: with two tabs the state that decides who reads this is off-screen
 * while you type.
 */

import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  PageHeader,
  Skeleton,
  Switch,
  TONE_BORDER,
  TONE_TEXT,
  TONE_TINT,
  Textarea,
  buttonClass,
  cn,
  formatDate,
  formatDateTime,
} from '@fiveox/ui'
import { Briefcase, Lock, MessageSquare, Send } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router'

import { CASE, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import { type ChatMessage, type Conversation, chat, noteStyle } from '../../lib/api/chat'

function MessageBubble({ message }: { message: ChatMessage }) {
  const style = noteStyle(message)
  const internal = style.tone === 'internal'
  return (
    <li
      data-internal={internal ? '' : undefined}
      className={cn(
        'rounded-xl border p-3',
        internal ? cn(TONE_TINT.warning, TONE_BORDER.warning) : 'border-border bg-card',
      )}
    >
      {style.label && (
        <div className={cn('mb-1 flex items-center gap-1 text-xs font-medium', TONE_TEXT.warning)}>
          <Lock size={12} aria-hidden /> {style.label}
        </div>
      )}
      <p className="text-foreground text-sm whitespace-pre-wrap">{message.body}</p>
      <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-2 text-xs">
        <span>
          {message.author_kind === 'person'
            ? 'The client'
            : message.author_kind === 'system'
              ? 'ProDesk'
              : 'The firm'}
        </span>
        {message.at && <span>{formatDateTime(message.at)}</span>}
        {message.attachment_ids.length > 0 && (
          <span>
            {message.attachment_ids.length} attachment
            {message.attachment_ids.length === 1 ? '' : 's'}
          </span>
        )}
        {message.broadcast_id && <Badge tone="neutral">Broadcast</Badge>}
      </div>
    </li>
  )
}

function InboxSkeleton() {
  return (
    <div role="status" aria-busy aria-label="Loading" className="grid gap-6 lg:grid-cols-[20rem_1fr]">
      <Skeleton className="h-72 w-full rounded-2xl" />
      <Skeleton className="hidden h-72 w-full rounded-2xl lg:block" />
    </div>
  )
}

export function Inbox() {
  const { can } = useEvaluation()
  const [params, setParams] = useSearchParams()
  const selected = params.get('thread')

  const [threads, setThreads] = useState<Conversation[]>([])
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [draft, setDraft] = useState('')
  const [internal, setInternal] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)

  const loadThreads = useCallback(async () => {
    const body = await chat.conversations()
    setThreads(body.data)
    setLoading(false)
  }, [])

  useEffect(() => {
    setLoading(true)
    void loadThreads().catch((err) => {
      setError(err instanceof ApiError ? err.message : 'Your messages could not be loaded.')
      setLoading(false)
    })
  }, [loadThreads])

  const loadThread = useCallback(async () => {
    if (!selected) {
      setMessages([])
      return
    }
    const body = await chat.messages(selected)
    setMessages(body.data.messages)
    // Clearing the badge is part of opening the thread, not a separate act —
    // a firm that has read something and still sees "1 unread" stops believing
    // the badge.
    await chat.markRead(selected, body.data.messages.at(-1)?.id)
    await loadThreads()
  }, [selected, loadThreads])

  useEffect(() => {
    void loadThread().catch((err) => {
      setError(err instanceof ApiError ? err.message : 'That conversation could not be opened.')
    })
  }, [loadThread])

  const send = async () => {
    if (!selected || !draft.trim()) return
    setBusy(true)
    setError(null)
    try {
      await chat.post(selected, { body: draft, is_internal: internal })
      setDraft('')
      await loadThread()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That message could not be sent.')
    } finally {
      setBusy(false)
    }
  }

  const open = (id: string) => {
    const copy = new URLSearchParams(params)
    copy.set('thread', id)
    setParams(copy, { replace: true })
  }

  const unread = threads.reduce((sum, thread) => sum + (thread.unread ?? 0), 0)
  const current = threads.find((thread) => thread.id === selected)

  return (
    <div className="w-full min-w-0 space-y-6">
      <PageHeader
        title="Messages"
        description={
          loading
            ? 'Every conversation with your clients, and the notes your firm keeps beside them.'
            : unread > 0
              ? `${unread} unread across ${threads.length} ${threads.length === 1 ? 'conversation' : 'conversations'}.`
              : 'Every conversation with your clients, and the notes your firm keeps beside them.'
        }
      />

      {error && <Alert tone="error">{error}</Alert>}

      {loading ? (
        <InboxSkeleton />
      ) : threads.length === 0 && !error ? (
        <EmptyState
          title="No conversations yet"
          description="A conversation starts from a case — open one and start it from there."
          action={
            <Link to="/cases" className={buttonClass({ variant: 'outline' })}>
              <Briefcase size={16} aria-hidden /> Go to your cases
            </Link>
          }
        />
      ) : (
        <div className="grid min-w-0 gap-6 lg:grid-cols-[20rem_minmax(0,1fr)]">
          <Card>
            <CardHeader title="Conversations" description={`${threads.length} open.`} />
            <CardBody>
              <ul className="space-y-2">
                {threads.map((thread) => (
                  <li key={thread.id}>
                    <button
                      type="button"
                      onClick={() => open(thread.id)}
                      aria-current={thread.id === selected ? 'true' : undefined}
                      className={cn(
                        'w-full rounded-lg border p-2.5 text-left transition-colors',
                        'focus-visible:ring-primary/40 focus-visible:ring-2 focus-visible:outline-none',
                        thread.id === selected
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:bg-muted/50',
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-foreground truncate text-sm font-medium">
                          {thread.subject ?? 'Direct'}
                        </span>
                        {thread.unread ? <Badge tone="warning">{thread.unread}</Badge> : null}
                      </div>
                      <div className="text-muted-foreground text-xs">
                        {thread.last_message_at
                          ? formatDate(thread.last_message_at)
                          : 'No messages yet'}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title={selected ? (current?.subject ?? 'Thread') : 'Pick a conversation'}
              description={
                selected
                  ? `${messages.length} ${messages.length === 1 ? 'message' : 'messages'}.`
                  : 'Choose one from the list.'
              }
            />
            <CardBody className="space-y-4">
              {!selected ? (
                <p className="text-muted-foreground flex items-center gap-2 text-sm">
                  <MessageSquare size={16} aria-hidden /> Nothing selected.
                </p>
              ) : (
                <>
                  {messages.length === 0 ? (
                    <p className="text-muted-foreground text-sm">Nothing has been said here yet.</p>
                  ) : (
                    <ul className="space-y-3">
                      {messages.map((message) => (
                        <MessageBubble key={message.id} message={message} />
                      ))}
                    </ul>
                  )}

                  {can(CASE, 'can_update') && (
                    <div
                      className={cn(
                        'space-y-3 border-t pt-4',
                        internal ? TONE_BORDER.warning : 'border-border',
                      )}
                    >
                      <Textarea
                        aria-label={internal ? 'Internal note' : 'Message to the client'}
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        rows={3}
                        placeholder={
                          internal
                            ? 'A note for the firm. The client will not see this.'
                            : 'A message to the client.'
                        }
                      />
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        {/* On the composer, not behind a tab: the state that
                            decides who reads this must be visible while you
                            type. */}
                        <Switch
                          checked={internal}
                          onChange={(event) => setInternal(event.target.checked)}
                          label="Internal note — the client cannot see this"
                        />
                        <Button
                          onClick={() => void send()}
                          disabled={busy || !draft.trim()}
                          loading={busy}
                        >
                          <Send size={14} aria-hidden /> {internal ? 'Save note' : 'Send'}
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </div>
      )}
    </div>
  )
}
